import ENV from "../env";
import { groupByTitle } from "../utils/grouping";
import { buildHeaders, handleExpiredLogin } from "./utils";

/** Mirrors the backend's CourseResponse; the catalogue lives in questions/courses/<id>/plan.yml. */
export type Course = {
    id: string;
    title: string;
    /** The heading the course is listed under in the catalogue. */
    group: string;
    cover?: string;
    questions: number;
    empty: boolean;
    /** The lesson groups in display order, each with the id a roadmap refers to it by. */
    groups: CourseLessonGroup[];
    lessons: Lesson[];
};

export type CourseLessonGroup = {
    id: string;
    title: string;
};

export type Importance = 'high' | 'mid' | 'low';

export type Lesson = {
    id: string;
    title: string;
    /** The title of the lesson's group. */
    group: string;
    /** The stable id of the lesson's group within its course. */
    groupId: string;
    importance: Importance;
    short?: string;
    description?: string;
    previous: string[];
    covers: string[];
    references: LessonReference[];
    questions: number;
    /** Whether those questions are enough to verify the lesson; the backend owns the minimum. */
    practicable: boolean;
    /** Whether the lesson has study material; only then is there something to read. */
    hasText: boolean;
};

export type LessonReference = {
    title: string;
    url: string;
};

/** Mirrors the backend's LessonTextResponse; the material lives in questions/courses/<id>/lessons/<lesson>.md. */
export type LessonText = {
    courseId: string;
    courseTitle: string;
    lessonId: string;
    title: string;
    group: string;
    short?: string;
    sections: LessonTextSection[];
};

/** One `#` heading of the file; `title` is missing for the text written before the first heading. */
export type LessonTextSection = {
    title?: string;
    body: string;
};

/** Lessons of one plan group, rebuilt on the client so the catalogue stays a flat list. */
export type LessonGroup = {
    title: string;
    lessons: Lesson[];
};

export default class CourseService {
    static courses(): Promise<Course[]> {
        return fetch(`${ ENV.BACKEND }/courses`, { method: 'GET', headers: buildHeaders() })
            .then((response) => handleExpiredLogin(response).json());
    }

    /** Rejects on an unknown course so the page can send the user to /404. */
    static course(courseId: string): Promise<Course> {
        return fetch(`${ ENV.BACKEND }/courses/${ courseId }`, { method: 'GET', headers: buildHeaders() })
            .then((response) => requireFound(handleExpiredLogin(response)).json());
    }

    /** Rejects when the course, the lesson or its text is missing, so the page can send the user to /404. */
    static lessonText(courseId: string, lessonId: string): Promise<LessonText> {
        const url = `${ ENV.BACKEND }/courses/${ courseId }/lessons/${ lessonId }/text`;

        return fetch(url, { method: 'GET', headers: buildHeaders() })
            .then((response) => requireFound(handleExpiredLogin(response)).json());
    }
}

function requireFound(response: Response): Response {
    if (!response.ok) {
        throw new Error(`Unable to load course, backend answered ${ response.status }`);
    }

    return response;
}

/** The path of the page that shows the study material of a lesson. */
export function lessonTextPath(courseId: string, lessonId: string): string {
    return `/courses/${ courseId }/lessons/${ lessonId }`;
}

/** Groups keep the order of their first lesson, which is the order the plan file lists them in. */
export function groupLessons(course: Course): LessonGroup[] {
    return groupByTitle(course.lessons, (lesson) => lesson.group)
        .map((group) => ({ title: group.title, lessons: group.items }));
}

/** Courses without a cover of their own fall back to a neutral one. */
export function coverUrl(course: Course): string {
    return `/assets/images/covers/${ course.cover || 'cs-general' }.svg`;
}
